import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import "../../../styles/legal-page.css";

// Shown in the "Contact us" section of both documents. Edit here once.
export const LEGAL_CONTACT = {
  name: "Global Tech9 LLC",
  email: "support@globaltech9.com",
  phone: "(+91) 79773 53388",
  phoneHref: "+917977353388",
  address:
    "Office no. 623, Centura Square IT Park, Thane, Maharashtra 400604, India",
};

const ICONS = {
  cal: '<rect x="3.5" y="5" width="17" height="15" rx="3"/><path d="M8 3v4M16 3v4M3.5 10h17"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  list: '<path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01"/>',
  print:
    '<path d="M7 9V4h10v5"/><path d="M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><path d="M7 14h10v6H7z"/>',
  arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
  chev: '<path d="M6 9l6 6 6-6"/>',
  crumb: '<path d="M9 6l6 6-6 6"/>',
  up: '<path d="M12 19V5M5.5 11.5L12 5l6.5 6.5"/>',
  mail: '<path d="M3 6h18v12H3zM3 7l9 6 9-6"/>',
  phone:
    '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>',
  pin: '<path d="M12 21s7-5.6 7-11a7 7 0 0 0-14 0c0 5.4 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/>',
  shield:
    '<path d="M12 3l8 3v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6z"/><path d="M8.5 12l2.5 2.5L16 9.5"/>',
  doc: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5M9 13h6M9 17h6"/>',
};

function Icon({ name, sw = 1.8 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
}

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

// text is a string, or an array mixing strings and { to, label } links
function Rich({ parts }) {
  if (typeof parts === "string") return parts;
  return parts.map((p, i) =>
    typeof p === "string" ? (
      <React.Fragment key={i}>{p}</React.Fragment>
    ) : (
      <Link key={i} to={p.to} className="gt9-lp__inline">
        {p.label}
      </Link>
    ),
  );
}

function Block({ block, contact }) {
  if (block.t === "p")
    return (
      <p>
        <Rich parts={block.text} />
      </p>
    );
  if (block.t === "note")
    return (
      <p className="gt9-lp__note">
        <Rich parts={block.text} />
      </p>
    );
  if (block.t === "ul")
    return (
      <ul>
        {block.items.map(([lead, text]) => (
          <li key={text}>
            {lead && <b>{lead}</b>} {text}
          </li>
        ))}
      </ul>
    );
  if (block.t === "contact")
    return (
      <div
        className={`gt9-lp__contact${contact.phone ? "" : " gt9-lp__contact--duo"}`}
      >
        <a className="gt9-lp__ci" href={`mailto:${contact.email}`}>
          <i>
            <Icon name="mail" />
          </i>
          <span>
            <small>Email</small>
            <b>{contact.email}</b>
          </span>
        </a>
        {contact.phone && (
          <a className="gt9-lp__ci" href={`tel:${contact.phoneHref || ""}`}>
            <i>
              <Icon name="phone" />
            </i>
            <span>
              <small>Phone</small>
              <b>{contact.phone}</b>
            </span>
          </a>
        )}
        <div className="gt9-lp__ci">
          <i>
            <Icon name="pin" />
          </i>
          <span>
            <small>{contact.name}</small>
            <b>{contact.address}</b>
          </span>
        </div>
      </div>
    );
  return null;
}

// rough reading time from the visible words
function readMinutes(intro, sections) {
  const text = (t) =>
    typeof t === "string"
      ? t
      : t.map((p) => (typeof p === "string" ? p : p.label)).join(" ");
  let words = intro.split(/\s+/).length;
  for (const s of sections) {
    words += s.title.split(/\s+/).length;
    for (const b of s.blocks) {
      if (b.text) words += text(b.text).split(/\s+/).length;
      if (b.items)
        for (const [lead, t] of b.items)
          words += `${lead} ${t}`.split(/\s+/).length;
    }
  }
  return Math.max(1, Math.round(words / 225));
}

export default function LegalPage({
  title,
  tagline,
  icon = "shield",
  effective,
  intro,
  sections,
  footnote,
  related,
  contact = LEGAL_CONTACT,
}) {
  const [active, setActive] = useState(sections[0].id);
  const [pct, setPct] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const rootRef = useRef(null);
  const docRef = useRef(null);
  const tocRef = useRef(null);
  const mnavRef = useRef(null);

  const minutes = useMemo(
    () => readMinutes(intro, sections),
    [intro, sections],
  );
  const words = title.split(" ");
  const last = words.pop();
  const index = Math.max(
    0,
    sections.findIndex((s) => s.id === active),
  );

  // title, start at the top (or at the section named in the address, e.g. /privacy-policy#cookies)
  useEffect(() => {
    document.title = `${title} | Global Tech9 LLC`;
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash && sections.some((s) => s.id === hash)) {
      requestAnimationFrame(() =>
        document.getElementById(hash)?.scrollIntoView(),
      );
    } else {
      window.scrollTo(0, 0);
    }
  }, [title, sections]);

  // reading progress, back-to-top button and the section being read
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const doc = docRef.current;
      if (!doc) return;
      const vh = window.innerHeight;
      const r = doc.getBoundingClientRect();
      const p = clamp(
        (-r.top + vh * 0.25) / Math.max(1, r.height - vh * 0.75),
        0,
        1,
      );
      rootRef.current?.style.setProperty("--p", p.toFixed(4));
      setPct(Math.round(p * 100));
      let current = sections[0].id;
      for (const s of sections) {
        const node = document.getElementById(s.id);
        // a section counts as current once it reaches the spot where the index jumps to (its scroll-margin) plus a little
        if (
          node &&
          node.getBoundingClientRect().top <=
            (parseFloat(getComputedStyle(node).scrollMarginTop) || 104) + 24
        )
          current = s.id;
      }
      if (r.bottom <= vh + 4) current = sections[sections.length - 1].id;
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sections]);

  // keep the highlighted entry of the (scrollable) side index in view
  useEffect(() => {
    const box = tocRef.current;
    const a = box?.querySelector('[aria-current="true"]');
    if (!box || !a || box.scrollHeight <= box.clientHeight) return;
    box.scrollTo({
      top: a.offsetTop - box.clientHeight / 2 + a.offsetHeight / 2,
      behavior: reducedMotion() ? "auto" : "smooth",
    });
  }, [active]);

  // phone index: close with Esc or a tap outside
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    const onDown = (e) =>
      mnavRef.current &&
      !mnavRef.current.contains(e.target) &&
      setNavOpen(false);
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [navOpen]);

  const goTo = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({
      behavior: reducedMotion() ? "auto" : "smooth",
      block: "start",
    });
    try {
      window.history.replaceState(null, "", `#${id}`);
    } catch (err) {
      /* ignore */
    }
    el.querySelector("h2")?.focus({ preventScroll: true });
    setNavOpen(false);
  }, []);

  const toc = (
    <ol>
      {sections.map((s, i) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            aria-current={active === s.id ? "true" : undefined}
            onClick={(e) => goTo(e, s.id)}
          >
            <span>{i + 1}</span>
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <main className="gt9-lp" ref={rootRef}>
      <div className="gt9-lp__bar" aria-hidden="true">
        <i />
      </div>

      {/* Hero */}
      <section className="gt9-lp__hero">
        <div className="gt9-container">
          <ol className="gt9-crumbs" aria-label="Breadcrumb">
            <li>
              <Link to="/">Home</Link>
              <Icon name="crumb" />
            </li>
            <li aria-current="page">{title}</li>
          </ol>
          <span className="gt9-eyebrow">Legal</span>
          <h1 className="gt9-lp__title">
            {words.join(" ")} <span className="hl">{last}</span>
          </h1>
          <p className="gt9-lp__lead">{tagline}</p>
          <ul className="gt9-lp__meta">
            <li>
              <Icon name="cal" />
              Effective date: {effective}
            </li>
            <li>
              <Icon name="clock" />
              {minutes} min read
            </li>
            <li>
              <Icon name="list" />
              {sections.length} sections
            </li>
            <li>
              <button
                type="button"
                className="gt9-lp__print"
                onClick={() => window.print()}
              >
                <Icon name="print" />
                Print
              </button>
            </li>
          </ul>
          <svg
            className="gt9-lp__mark"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.55"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: ICONS[icon] }}
          />
        </div>
      </section>

      <div className="gt9-lp__body">
        <div className="gt9-container">
          {/* Tablet and phone: sticky index */}
          <div
            className={`gt9-lp__mnav${navOpen ? " is-open" : ""}`}
            ref={mnavRef}
          >
            <button
              type="button"
              className="gt9-lp__mbtn"
              aria-expanded={navOpen}
              aria-controls="gt9-lp-mlist"
              onClick={() => setNavOpen((o) => !o)}
            >
              <span className="gt9-lp__mno">{index + 1}</span>
              <span className="gt9-lp__mtxt">
                <small>
                  Section {index + 1} of {sections.length}
                </small>
                <b>{sections[index].title}</b>
              </span>
              <Icon name="chev" sw={2} />
            </button>
            <nav
              className="gt9-lp__mlist"
              id="gt9-lp-mlist"
              aria-label={`${title} sections`}
            >
              {toc}
            </nav>
          </div>

          <div className="gt9-lp__layout">
            <aside className="gt9-lp__side">
              <nav className="gt9-lp__toc" aria-label={`${title} sections`}>
                <div className="gt9-lp__toc-head">
                  <h2>On this page</h2>
                  <span aria-label={`${pct} percent read`}>{pct}%</span>
                </div>
                <div className="gt9-lp__meter" aria-hidden="true">
                  <i />
                </div>
                <div className="gt9-lp__toc-list" ref={tocRef}>
                  {toc}
                </div>
              </nav>
            </aside>

            <article className="gt9-lp__doc" ref={docRef}>
              <p className="gt9-lp__intro">{intro}</p>
              {sections.map((s, i) => (
                <section
                  className="gt9-lp__sec"
                  id={s.id}
                  key={s.id}
                  aria-labelledby={`${s.id}-h`}
                >
                  <header className="gt9-lp__sh">
                    <span className="gt9-lp__no" aria-hidden="true">
                      {i + 1}
                    </span>
                    <h2 id={`${s.id}-h`} tabIndex={-1}>
                      {s.title}
                    </h2>
                  </header>
                  <div className="gt9-lp__sb">
                    {s.blocks.map((b, k) => (
                      <Block block={b} contact={contact} key={k} />
                    ))}
                  </div>
                </section>
              ))}
              <footer className="gt9-lp__end">
                <p>{footnote}</p>
              </footer>
            </article>
          </div>

          {/* Also read */}
          <section className="gt9-lp__next" aria-labelledby="gt9-lp-next-t">
            <div>
              <span className="gt9-lp__kicker">Also read</span>
              <h2 id="gt9-lp-next-t">{related.title}</h2>
              <p>{related.text}</p>
            </div>
            <Link to={related.to} className="gt9-lp__btn">
              Read {related.title}
              <Icon name="arrow" sw={2} />
            </Link>
          </section>
        </div>
      </div>
    </main>
  );
}
